import React, { useMemo, useRef } from 'react';
import { Box, Container, Typography, Card, CardContent, IconButton, Avatar } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { colors } from './Theme/WebTheme';
import { FONT_FAMILY } from '../common/sharedFunctions';
import PersonIcon from '@mui/icons-material/Person';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

const FleetSlider = React.memo(() => {
  const scrollRef = useRef(null);
  const usersdata = useSelector(state => state.usersdata);
  const auth = useSelector(state => state.auth);
  const { i18n } = useTranslation();

  // Check if user is logged in
  const isLoggedIn = useMemo(() => !!(auth?.profile?.uid), [auth?.profile?.uid]);

  const langCode = useMemo(() => 
    (i18n?.language ? i18n.language.slice(0, 2).toLowerCase() : 'fr'),
    [i18n?.language]
  );

  const fleetContent = useMemo(() => ({
    fr: {
      title: 'Notre flotte hybride et électrique',
      subtitle: 'Une mobilité plus responsable, selon les véhicules disponibles',
      description: 'Chez Allô Driver, nous privilégions les véhicules hybrides et électriques lorsque cela est possible. L’objectif est de proposer des trajets confortables tout en réduisant progressivement l’impact environnemental de la flotte.',
      rseHighlights: [
        '🌱 Véhicules hybrides et électriques',
        '♻️ Démarche de réduction des émissions',
        '🔋 Suivi de l\'évolution de la flotte',
        '🌍 Mobilité locale plus responsable'
      ],
      certifiedVehicle: 'Véhicule suivi',
      trainingNote: 'Chauffeurs formés à l\'éco-conduite et aux standards Allô Driver',
      badge1: '✓ Formation éco-conduite',
      badge2: '✓ Véhicules hybrides/électriques',
      badge3: '✓ Service suivi'
    },
    en: {
      title: 'Our 100% Eco-Responsible Premium Fleet',
      subtitle: 'CSR Commitment: Zero Emissions, 100% Comfort',
      description: 'At Allô Driver, we actively participate in transportation electrification. Our fleet consists exclusively of hybrid and electric vehicles to reduce our carbon footprint. Every trip with us is a gesture for the environment, without compromising on comfort and service quality.',
      rseHighlights: [
        '🌱 100% hybrid & electric fleet',
        '♻️ -80% CO₂ emissions vs thermal vehicles',
        '🔋 Green charging via renewable energy',
        '🌍 Active contribution to ecological transition'
      ],
      certifiedVehicle: 'Eco-Certified Vehicle',
      trainingNote: 'Drivers trained in eco-driving and Allô Driver standards',
      badge1: '✓ Eco-driving training',
      badge2: '✓ Zero emission vehicles',
      badge3: '✓ Sustainable excellence service'
    }
  }), []);

  const content = useMemo(() => 
    fleetContent[langCode] || fleetContent.fr,
    [fleetContent, langCode]
  );

  const allDrivers = useMemo(() => {
    if (!usersdata || !usersdata.users) return [];
    
    const approvedDrivers = Object.values(usersdata.users)
      .filter(user => 
        user.usertype === 'driver' && 
        user.approved === true && 
        user.vehicleMake &&
        user.vehicleModel
      )
      .map(driver => ({
        firstName: driver.firstName || 'Chauffeur',
        vehicleMake: driver.vehicleMake,
        vehicleModel: driver.vehicleModel,
        profilePic: driver.profile_image
      }));

    // Shuffle and limit to 12 drivers
    const shuffled = [...approvedDrivers].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 12);
  }, [usersdata]);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 300;
      scrollRef.current.scrollLeft += direction === 'left' ? -scrollAmount : scrollAmount;
    }
  };

  // Don't render if user is not logged in
  if (!isLoggedIn) {
    return null;
  }

  return (
    <Box sx={{ 
      py: { xs: 8, md: 12 }, 
      px: { xs: 2, md: 4 },
      backgroundColor: '#000',
      position: 'relative'
    }}>
      <Container maxWidth="lg">
        {/* Fleet Title */}
        <Typography
          variant="h2"
          sx={{
            fontFamily: FONT_FAMILY,
            fontSize: { xs: '28px', md: '38px' },
            fontWeight: 700,
            color: '#fff',
            mb: 1,
            textAlign: 'center'
          }}
        >
          {content.title}
        </Typography>
        <Typography
          variant="h5"
          sx={{
            fontFamily: FONT_FAMILY,
            fontSize: { xs: '16px', md: '20px' },
            fontWeight: 600,
            color: colors.Button_Primary,
            mb: 3,
            textAlign: 'center'
          }}
        >
          {content.subtitle}
        </Typography>
        <Typography
          sx={{
            fontFamily: FONT_FAMILY,
            fontSize: { xs: '15px', md: '17px' },
            color: 'rgba(255,255,255,0.85)',
            lineHeight: 1.8,
            textAlign: 'center',
            maxWidth: '900px',
            margin: '0 auto 32px'
          }}
        >
          {content.description}
        </Typography>

        {/* RSE Highlights */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
            gap: 2,
            maxWidth: '1000px',
            margin: '0 auto 48px',
            px: { xs: 2, md: 0 }
          }}
        >
          {content.rseHighlights.map((highlight, index) => (
            <Box
              key={index}
              sx={{
                backgroundColor: 'rgba(212, 175, 55, 0.08)',
                border: '1px solid rgba(212, 175, 55, 0.2)',
                borderRadius: '12px',
                padding: { xs: '12px', md: '16px' },
                textAlign: 'center',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(212, 175, 55, 0.12)',
                  borderColor: 'rgba(212, 175, 55, 0.4)',
                  transform: 'translateY(-4px)'
                }
              }}
            >
              <Typography
                sx={{
                  fontFamily: FONT_FAMILY,
                  fontSize: { xs: '13px', md: '14px' },
                  color: 'rgba(255,255,255,0.9)',
                  fontWeight: 500,
                  lineHeight: 1.5
                }}
              >
                {highlight}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Drivers Slider */}
        <Box sx={{ position: 'relative' }}>
          {/* Navigation Buttons */}
          <IconButton
            onClick={() => scroll('left')}
            sx={{
              position: 'absolute',
              left: { xs: -10, md: -20 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              backgroundColor: 'rgba(212, 175, 55, 0.1)',
              color: colors.Button_Primary,
              width: 48,
              height: 48,
              border: '1px solid rgba(212, 175, 55, 0.3)',
              '&:hover': {
                backgroundColor: 'rgba(212, 175, 55, 0.2)',
                border: '1px solid rgba(212, 175, 55, 0.5)',
              },
              display: { xs: 'none', md: 'flex' }
            }}
          >
            <ArrowBackIosNewIcon />
          </IconButton>

          <IconButton
            onClick={() => scroll('right')}
            sx={{
              position: 'absolute',
              right: { xs: -10, md: -20 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              backgroundColor: 'rgba(212, 175, 55, 0.1)',
              color: colors.Button_Primary,
              width: 48,
              height: 48,
              border: '1px solid rgba(212, 175, 55, 0.3)',
              '&:hover': {
                backgroundColor: 'rgba(212, 175, 55, 0.2)',
                border: '1px solid rgba(212, 175, 55, 0.5)',
              },
              display: { xs: 'none', md: 'flex' }
            }}
          >
            <ArrowForwardIosIcon />
          </IconButton>

          {/* Scrollable Container */}
          <Box
            ref={scrollRef}
            sx={{
              display: 'flex',
              gap: 3,
              overflowX: 'auto',
              scrollBehavior: 'smooth',
              pb: 2,
              '&::-webkit-scrollbar': {
                height: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(255,255,255,0.05)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: colors.Button_Primary,
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: '#c4a137',
                },
              },
            }}
          >
            {allDrivers.map((driver, index) => (
              <Card 
                key={index} 
                sx={{
                  minWidth: 280,
                  maxWidth: 280,
                  background: 'linear-gradient(145deg, #1a1a1a 0%, #0a0a0a 100%)',
                  borderRadius: '16px',
                  border: '1px solid rgba(212, 175, 55, 0.2)',
                  transition: 'all 0.3s ease',
                  backgroundColor: 'transparent',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    borderColor: colors.Button_Primary,
                    boxShadow: `0 12px 40px rgba(212, 175, 55, 0.25)`
                  }
                }}
              >
                <CardContent sx={{ padding: 2, '&:last-child': { paddingBottom: 2 }, backgroundColor: 'transparent' }}>
                  {/* Driver Info */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Avatar
                      src={driver.profilePic}
                      sx={{
                        width: 40,
                        height: 40,
                        border: `2px solid ${colors.Button_Primary}`,
                        bgcolor: 'rgba(212, 175, 55, 0.2)'
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 24 }} />
                    </Avatar>
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        fontSize: '16px',
                        fontWeight: 700,
                        color: '#fff'
                      }}
                    >
                      {driver.firstName}
                    </Typography>
                  </Box>

                  {/* Vehicle Info */}
                  <Box
                    sx={{
                      background: 'rgba(212, 175, 55, 0.1)',
                      borderRadius: '8px',
                      padding: '12px',
                      border: '1px solid rgba(212, 175, 55, 0.2)'
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        fontSize: '14px',
                        color: colors.Button_Primary,
                        fontWeight: 600,
                        mb: 0.5
                      }}
                    >
                      {content.certifiedVehicle}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        fontSize: '15px',
                        color: '#fff',
                        fontWeight: 600
                      }}
                    >
                      {driver.vehicleMake} {driver.vehicleModel}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        {/* CTA */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: '14px',
              color: 'rgba(255,255,255,0.6)',
              mb: 2
            }}
          >
            {content.trainingNote}
          </Typography>
          <Box
            sx={{
              display: 'inline-flex',
              gap: 2,
              padding: '12px 24px',
              background: 'rgba(212, 175, 55, 0.1)',
              borderRadius: '12px',
              border: '1px solid rgba(212, 175, 55, 0.2)',
              flexWrap: 'wrap',
              justifyContent: 'center'
            }}
          >
            <Typography
              sx={{
                fontFamily: FONT_FAMILY,
                fontSize: '13px',
                color: colors.Button_Primary,
                fontWeight: 600
              }}
            >
              {content.badge1}
            </Typography>
            <Typography
              sx={{
                fontFamily: FONT_FAMILY,
                fontSize: '13px',
                color: colors.Button_Primary,
                fontWeight: 600
              }}
            >
              {content.badge2}
            </Typography>
            <Typography
              sx={{
                fontFamily: FONT_FAMILY,
                fontSize: '13px',
                color: colors.Button_Primary,
                fontWeight: 600
              }}
            >
              {content.badge3}
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
});

export default FleetSlider;
